import { BellSimple, Checks, Icon, Info, ReadCvLogo, Warning } from '@phosphor-icons/react';
import { t } from 'i18next';
import { max, uniqBy } from 'lodash';
import { InfiniteData } from 'react-query';

import { BellNotificationSeverity, BellNotificationType } from 'notifications/enums';
import {
  IBellNotification,
  IBellNotificationAlertColors,
  IBellNotificationContent,
  IBellNotificationCta,
  IBellNotificationHandler,
  IBellNotificationSeverityIcon,
  IBellNotificationsResponse,
  TBellNotificationHandlers,
} from 'notifications/types';
import { BASE_ROUTE } from 'router/constants';
import { Color } from 'theme/enums';

type TNotificationPages = InfiniteData<IBellNotificationsResponse>;

// Pages overlap when new notifications shift the offsets between two page loads.
export const getUniqueNotifications = (data: TNotificationPages | undefined): IBellNotification[] =>
  uniqBy(data?.pages?.flatMap((page) => page?.notifications ?? []) ?? [], 'id');

// Ids grow with creation time: the highest loaded one is the newest notification the user saw.
export const getHighestNotificationId = (notifications: IBellNotification[]): number | null =>
  max(notifications?.map((notification) => notification?.id)) ?? null;

// `ids` null means every notification.
export const countUnreadNotifications = (
  data: TNotificationPages | undefined,
  ids: number[] | null
): number =>
  getUniqueNotifications(data).filter(
    (notification) => !notification.isRead && (!ids || ids.includes(notification.id))
  ).length;

// A notification loaded in both the All and the Unread list counts once.
export const countUnreadNotificationsIn = (
  dataSets: (TNotificationPages | undefined)[],
  ids: number[] | null
): number =>
  uniqBy(dataSets.flatMap(getUniqueNotifications), 'id').filter(
    (notification) => !notification.isRead && (!ids || ids.includes(notification.id))
  ).length;

export const markNotificationPagesRead = (
  data: TNotificationPages | undefined,
  ids: number[] | null,
  readAt: string
): TNotificationPages | undefined => {
  if (!data) {
    return data;
  }

  const unreadDelta = countUnreadNotifications(data, ids);

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      notifications: page.notifications.map((notification) =>
        notification.isRead || (ids && !ids.includes(notification.id))
          ? notification
          : { ...notification, isRead: true, readAt }
      ),
      unreadCount: ids ? Math.max(0, page.unreadCount - unreadDelta) : 0,
    })),
  };
};

export const markNotificationPagesUnread = (
  data: TNotificationPages | undefined,
  id: number
): TNotificationPages | undefined => {
  if (!data) {
    return data;
  }

  const wasRead = getUniqueNotifications(data).some(
    (notification) => notification.id === id && notification.isRead
  );

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      notifications: page.notifications.map((notification) =>
        notification.id === id && notification.isRead
          ? { ...notification, isRead: false, readAt: null }
          : notification
      ),
      unreadCount: wasRead ? page.unreadCount + 1 : page.unreadCount,
    })),
  };
};

export const removeNotificationPages = (
  data: TNotificationPages | undefined,
  ids: number[] | null
): TNotificationPages | undefined => {
  if (!data) {
    return data;
  }

  const removed = getUniqueNotifications(data).filter(
    (notification) => !ids || ids.includes(notification.id)
  ).length;
  const unreadDelta = countUnreadNotifications(data, ids);

  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      notifications: ids
        ? page.notifications.filter((notification) => !ids.includes(notification.id))
        : [],
      total: ids ? Math.max(0, page.total - removed) : 0,
      unreadCount: ids ? Math.max(0, page.unreadCount - unreadDelta) : 0,
    })),
  };
};

// The BE sends site-relative paths including the router basename (/v2/order/1/parts);
// react-router adds the basename itself, so navigate()/RouterLink need it stripped.
export const toRouterPath = (url: string | null | undefined): string | null =>
  url?.startsWith(`${BASE_ROUTE}/`) ? url.slice(BASE_ROUTE.length) : null;

const GENERIC_HANDLER: IBellNotificationHandler = {
  getActions: () => [],
  getContent: () => ({
    title: t('titleBellNotificationGeneric'),
    subtitle: null,
    severity: BellNotificationSeverity.INFO,
  }),
  getInternalPath: () => null,
};

let handlers: Partial<TBellNotificationHandlers> = {};

// notifications/handlers registers one handler per type at startup (Router imports it). The
// registry keeps handler imports out of this file: the handlers import toRouterPath from here.
export const registerNotificationHandlers = (registry: TBellNotificationHandlers) => {
  handlers = registry;
};

// Unknown types (a BE ahead of this FE) render as a generic item instead of crashing. The cast
// is TypeScript's correlated-union limit: each entry only ever receives its own type.
const getHandler = (notification: IBellNotification): IBellNotificationHandler =>
  (handlers[notification?.type] as IBellNotificationHandler) ?? GENERIC_HANDLER;

export const getNotificationContent = (notification: IBellNotification): IBellNotificationContent =>
  getHandler(notification).getContent(notification);

export const getNotificationActions = (notification: IBellNotification): IBellNotificationCta[] =>
  getHandler(notification).getActions(notification);

// Router path (no basename) of the page the notification is about.
export const getNotificationInternalPath = (notification: IBellNotification): string | null =>
  getHandler(notification).getInternalPath(notification);

// The Alert design's glyphs. Numbered steps, not plainColor: the dark scale is inverted
// (theme.tsx), so step 600 stays legible on the tinted cards in both modes.
const SEVERITY_ICONS: Record<BellNotificationSeverity, IBellNotificationSeverityIcon> = {
  [BellNotificationSeverity.DEFAULT]: { color: 'neutral.600', icon: BellSimple },
  [BellNotificationSeverity.ERROR]: { color: 'danger.600', icon: Warning },
  [BellNotificationSeverity.INFO]: { color: 'primary.600', icon: Info },
  [BellNotificationSeverity.SUCCESS]: { color: 'success.600', icon: Checks },
  [BellNotificationSeverity.WARNING]: { color: 'warning.600', icon: Warning },
};

export const getNotificationSeverityIcon = (
  severity: BellNotificationSeverity
): IBellNotificationSeverityIcon =>
  SEVERITY_ICONS[severity] ?? SEVERITY_ICONS[BellNotificationSeverity.INFO];

// The drawer row's glyph is the notification type's (the drawer design), coloured by severity; the
// toast keeps the severity glyph (the Alert design).
const TYPE_ICONS: Record<BellNotificationType, Icon> = {
  [BellNotificationType.EXTERNAL_ORDER_UPDATE]: ReadCvLogo,
};

// A type this FE does not know yet (the BE is ahead) falls back to the severity glyph.
export const getNotificationTypeIcon = (
  type: BellNotificationType | string,
  severity: BellNotificationSeverity
): Icon => TYPE_ICONS[type as BellNotificationType] ?? getNotificationSeverityIcon(severity).icon;

const SEVERITY_COLORS: Record<BellNotificationSeverity, Color> = {
  [BellNotificationSeverity.DEFAULT]: Color.NEUTRAL,
  [BellNotificationSeverity.ERROR]: Color.DANGER,
  [BellNotificationSeverity.INFO]: Color.PRIMARY,
  [BellNotificationSeverity.SUCCESS]: Color.SUCCESS,
  [BellNotificationSeverity.WARNING]: Color.WARNING,
};

// The palette the drawer row's round icon and the toast card are tinted with.
export const getNotificationSeverityColor = (severity: BellNotificationSeverity): Color =>
  SEVERITY_COLORS[severity] ?? Color.PRIMARY;

// The Alert design: a light tint with a coloured border, the info border one step lighter. The
// dark scheme inverts the numbered steps (theme.tsx), so the same tokens give a dark tint there.
export const getNotificationAlertColors = (
  severity: BellNotificationSeverity
): IBellNotificationAlertColors => {
  const color = getNotificationSeverityColor(severity);

  if (Color.NEUTRAL === color) {
    return { backgroundColor: 'background.surface', borderColor: 'neutral.outlinedBorder' };
  }

  return {
    backgroundColor: `${color}.50`,
    borderColor: `${color}.${Color.PRIMARY === color ? 300 : 400}`,
  };
};
